import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

// Main Container for the book review
const BookReviewContainer = styled.div`
  padding: 6rem 1.5rem 5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgb(var(--bg));
  min-height: 100vh;
  font-family: 'Arial', sans-serif;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 10% 0%, rgba(139, 130, 196, 0.12), transparent 40%);
    pointer-events: none;
  }

  & > * {
    position: relative;
    z-index: 1;
  }
`;

// Title styling
const Title = styled.h1`
  font-size: 2.5rem;
  color: #8b82c4;
  text-align: center;
  margin-bottom: 1rem;
  line-height: 1.2;
`;

// Author styling
const Author = styled.p`
  font-size: 1.2rem;
  color: rgb(var(--fg) / 0.78);
  margin-bottom: 1rem;
  font-style: italic;
`;

// Cover Image styling
const CoverImage = styled.img`
  width: 100%;
  max-width: 300px;
  max-height: 600px;
  border-radius: 8px;
  margin-bottom: 1rem;
  box-shadow: 0 20px 40px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  border: 1px solid rgb(var(--fg) / 0.08);
`;

// Date Published styling
const DatePublished = styled.p`
  font-size: 1rem;
  color: rgb(var(--gray) / 0.55);
  margin-bottom: 2rem;
`;

// Content Section styling
const ContentSection = styled.div`
  width: 100%;
  max-width: 1200px;
  margin-bottom: 2rem;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  padding: 2rem;
  border-radius: 20px;
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
`;

// Section Title styling
const SectionTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: #8b82c4;
  border-bottom: 1px solid rgba(139, 130, 196, 0.3);
  padding-bottom: 0.5rem;
`;

// Section Content styling
const SectionContent = styled.div`
  font-size: 1rem;
  line-height: 1.8;
  color: rgb(var(--fg) / 0.82);
`;

// Quote styling
const Quote = styled.blockquote`
  font-style: italic;
  margin: 1.5rem 0;
  padding: 1rem;
  background: rgb(var(--fg) / 0.04);
  border-left: 3px solid #8b82c4;
  border-radius: 8px;
  color: rgb(var(--fg) / 0.78);
`;

// List Item styling
const ListItem = styled.li`
  margin-bottom: 1rem;
  font-size: 1rem;
  color: rgb(var(--fg) / 0.82);
`;

// Ordered List styling
const OrderedList = styled.ol`
  padding-left: 1rem;
  color: rgb(var(--fg) / 0.82);
`;

// Tags Container styling
const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  max-width: 800px;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

// Tag styling
const Tag = styled.span`
  background: rgb(var(--fg) / 0.05);
  border: 1px solid rgba(139, 130, 196, 0.3);
  color: rgb(var(--fg) / 0.9);
  padding: 0.4rem 0.7rem;
  border-radius: 12px;
  font-size: 0.8rem;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
`;

const Button = styled.button`
  display: inline-block;
  margin: 2rem 0;
  padding: 0.9rem 2rem;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgb(var(--fg) / 0.92);
  background: rgb(var(--fg) / 0.06);
  border: 1px solid rgb(var(--fg) / 0.14);
  border-radius: 999px;
  cursor: pointer;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: transform 0.25s, border-color 0.25s, background 0.25s;
  text-align: center;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.1);
    border-color: rgba(139, 130, 196, 0.5);
  }
`;

export const WhereGoodIdeasComeFromMetadata = {
  title: "Where Good Ideas Come From",
  description: "The Natural History of Innovation",
  tags: ['Innovation', 'Creativity', 'Science', 'Technology'],
  author: "Steven Johnson",
  datePublished: "2023-11-02",
  category: "Book Review",
  path: "/book-review/where-good-ideas-come-from",
  coverImage: "/images/where_good_ideas_come_from.jpg", // Ensure this path is correct
};

const WhereGoodIdeasComeFrom = () => {
  const navigate = useNavigate();
  const metadata = WhereGoodIdeasComeFromMetadata;

  return (
    <BookReviewContainer>
      <Title>{metadata.title}</Title>
      <Author>by {metadata.author}</Author>
      <CoverImage src={metadata.coverImage} alt={metadata.title} />
      <DatePublished>Date Published: {metadata.datePublished}</DatePublished>
      <TagContainer>
          {metadata.tags.map((tag, index) => (
            <Tag key={index}>{tag}</Tag>
          ))}
      </TagContainer>
      <br />
      <br />

      <ContentSection>
        <SectionTitle>Summary</SectionTitle>
        <SectionContent>
          <p>
            In "Where Good Ideas Come From", Steven Johnson explores the origins of innovation and creativity, uncovering
            the patterns that lead to breakthroughs in technology, science, and art. He emphasizes the importance of connectivity,
            collaboration, and openness in fostering a culture of innovation.
          </p>
          <br />
          <p>
            Johnson delves into historical examples and current theories, showing how ideas develop in networks and ecosystems
            rather than in isolation. He argues that environments where diverse concepts can collide and recombine are the most
            conducive to generating groundbreaking ideas.
          </p>
          <br />
          <p>
            Through this insightful book, readers gain an understanding of how we can cultivate our own creativity and harness
            the power of collaborative networks to drive progress and evolution in various fields.
          </p>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Key Themes or Concepts</SectionTitle>
        <SectionContent>
          <OrderedList>
            <ListItem><strong>The Adjacent Possible:</strong> The concept of expanding the boundaries of innovation by exploring adjacent ideas and opportunities.</ListItem>
            <ListItem><strong>Liquid Networks:</strong> The power of collaboration and idea sharing in dynamic, flexible environments.</ListItem>
            <ListItem><strong>Slow Hunch:</strong> How long incubation periods can lead to breakthroughs as ideas gradually mature.</ListItem>
            <ListItem><strong>Serendipity:</strong> The role of unexpected connections and discoveries in innovation.</ListItem>
            <ListItem><strong>Error and Experimentation:</strong> Embracing failure and experimentation as essential to the creative process.</ListItem>
          </OrderedList>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Favorite Quotes</SectionTitle>
        <SectionContent>
          <Quote>
            "Chance favors the connected mind."
          </Quote>
          <Quote>
            "The trick to having good ideas is not to sit around in glorious isolation and try to think big thoughts. The trick is to get more parts on the table."
          </Quote>
        </SectionContent>
      </ContentSection>

      {/* Personal Reflection Section */}
      <ContentSection>
        <SectionTitle>Personal Reflection</SectionTitle>
        <SectionContent>
          <p>
            Reading "Where Good Ideas Come From" made me rethink how creativity works. Steven Johnson’s breakdown of concepts like
            "the adjacent possible" and "liquid networks" gave me a fresh perspective on how ideas don’t usually come out of nowhere,
            but rather evolve from existing contexts, collaborations, and connections.
          </p>
          <br />
          <p>
            What really struck me was the concept of the "slow hunch"—the idea that sometimes good ideas take time to form and mature,
            often lingering in the background before they finally become clear. This shifted my mindset about how I approach my own ideas.
            Instead of forcing quick results, I now see the value in letting ideas simmer and develop over time, knowing that the right
            connections will eventually come together.
          </p>
          <br />
          <p>
            Another powerful takeaway for me was the role of serendipity in the creative process. Johnson’s focus on unexpected,
            sometimes random discoveries reminded me that good ideas don’t always follow a straight path. They often come from
            chance encounters, mistakes, or unplanned connections. This made me more open to exploring ideas that might seem
            unrelated at first but could later become the key to something larger.
          </p>
          <br />
          <p>
            Overall, this book shifted my approach to problem-solving and creativity. It reminded me to embrace collaboration,
            stay open to serendipity, and trust that great ideas often come from the most unexpected sources.
          </p>
        </SectionContent>
      </ContentSection>

      <Button onClick={() => navigate('/blog')}>Back to Blog</Button>
    </BookReviewContainer>
  );
};

export default WhereGoodIdeasComeFrom;
